import {
  useState, useEffect, useCallback, useRef,
} from 'react';

import {
  getWebhookEndpoints, getWebhookEventKeys, createWebhookEndpoint, updateWebhookEndpoint,
  deleteWebhookEndpoint, testWebhookEndpoint, rotateWebhookSecret, getWebhookDeliveries,
  replayWebhookDelivery,
} from '../../../services/webhookService';
import { extractListData } from '../../../utils/Utils';
import { moduleName } from '../../../utils/moduleNames';

// Entegrasyonlar > Webhook'lar (Public API Plan 03, Görev 7; tasarım belgesi §6).
//
// DÜZ SIR BİR KEZ: sunucu sırrı üretir ve YALNIZ oluşturma / sır yenileme yanıtında döner.
// Bu kanca onu HİÇBİR state'te tutmaz: yanıt çağırana döner, liste her zaman liste ucundan
// (maskeli) yeniden çekilir. Gösterimin ömrü sekmenin "bir kez göster" paneliyle sınırlıdır.

// Backend `webhook.nameRequired` — ad sınırı DB kolonunun (200) aynısı.
export const WEBHOOK_NAME_MAX = 200;

export const DELIVERY_STATUSES = ['pending', 'failed', 'success', 'dlq'];

export const DELIVERY_STATUS_COLORS = {
  pending: { color: '#64748B', bg: '#F1F5F9' },
  success: { color: '#16A34A', bg: '#DCFCE7' },
  failed: { color: '#EA580C', bg: '#FFF7ED' },
  dlq: { color: '#E53935', bg: '#FEF2F2' },
};

// Uç durumu: sistem kapattı (autoDisabledAt) > İK kapattı (isEnabled=false) > aktif.
export function endpointState(row) {
  if (!row?.isEnabled && row?.autoDisabledAt) return 'autoDisabled';
  if (!row?.isEnabled) return 'disabled';
  return 'enabled';
}

export const ENDPOINT_STATE_COLORS = {
  enabled: { color: '#16A34A', bg: '#DCFCE7' },
  disabled: { color: '#64748B', bg: '#F1F5F9' },
  autoDisabled: { color: '#E53935', bg: '#FEF2F2' },
};

// Olay adı: Bildirim Şablonları ekranının 5 dilli etiketleri (`performance.notification.event.*`)
// — yeni çeviri ailesi açılmaz. Etiket yoksa sunucunun verdiği varsayılan konu, o da yoksa anahtar.
export function eventLabel(t, eventKey, fallback) {
  return t(`performance.notification.event.${eventKey}`, { defaultValue: fallback || eventKey });
}

// Olay seçicisi — katalog 60+ olay: düz liste kullanılamaz, arama ŞART. Seçenek adı
// "Modül · Olay" (ör. "İşe Alım · Mülakat Planlandı"); arama ad, modül ve anahtar üzerinde.
// GenericFormDrawer'ın asyncSelect alanı YALNIZ onSearch/onGetById okur (asyncConfig ölü alan);
// bu yüzden seçici KABUK içinde AsyncGenericSelect ile ve bu iki fonksiyonla beslenir.
export function buildEventAsync(t, catalog) {
  const options = (catalog || []).map((e) => ({
    id: e.eventKey,
    name: `${moduleName(t, e.moduleCode)} · ${eventLabel(t, e.eventKey, e.label)}`,
  }));
  const byId = new Map(options.map((o) => [o.id, o]));
  return {
    options,
    onSearch: async (term) => {
      if (!term) return options;
      const needle = term.toLocaleLowerCase('tr');
      return options.filter((o) => o.name.toLocaleLowerCase('tr').includes(needle)
        || o.id.toLocaleLowerCase('tr').includes(needle));
    },
    // Artık listede olmayan (ör. kiracı modülü bıraktı) kayıtlı anahtar da okunur görünsün.
    onGetById: async (id) => byId.get(id) || { id, name: eventLabel(t, id) },
  };
}

// "%96,5" — dil biçimiyle; oran yoksa (son 24 saatte denenmiş teslimat yok) null.
export function formatSuccessRate(rate, lang) {
  if (rate === null || rate === undefined) return null;
  return new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 1 }).format(rate / 100);
}

// Savunma: liste satırı hiçbir koşulda düz sır taşımaz — sunucu yanlışlıkla döndürse bile.
// eslint-disable-next-line no-unused-vars
const toListRow = ({ plainSecret, ...rest }) => rest;

// Oluşturma/yenileme yanıtı: düz sır gelmediyse başarı sayılmaz (401'de interceptor
// mutasyonu da boş yanıtla ÇÖZER, reject etmez).
const toCreated = (res) => {
  const created = res?.data?.data;
  return res?.data?.status && created?.plainSecret ? created : null;
};

export function useWebhooksApi() {
  const [fetching, setFetching] = useState(true);
  const [failed, setFailed] = useState(false);
  const [data, setData] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const requestSeq = useRef(0);

  useEffect(() => {
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setFetching(true);
    getWebhookEndpoints()
      .then((res) => {
        if (seq !== requestSeq.current) return;
        // api.js GET okumalarında reject ETMEZ — gerçek sinyal res.data.status.
        // "Yüklenemedi" ile "hiç uç yok" AYRI durumlardır.
        if (!res?.data?.status) {
          setFailed(true);
          setData([]);
          return;
        }
        setFailed(false);
        setData(extractListData(res).map(toListRow));
      })
      .finally(() => { if (seq === requestSeq.current) setFetching(false); });
  }, [refreshTrigger]);

  // Katalog kiracıya göre süzülür (abone olunan modüller) — bir kez çekilir.
  useEffect(() => {
    getWebhookEventKeys().then((res) => {
      setCatalog(res?.data?.status ? extractListData(res) : []);
    });
  }, []);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const create = useCallback(async (payload) => {
    try {
      const created = toCreated(await createWebhookEndpoint(payload));
      if (created) refresh();
      return created;
    } catch {
      return null;   // hata toast'ı interceptor'da
    }
  }, [refresh]);

  const update = useCallback(async (id, payload) => {
    try {
      const res = await updateWebhookEndpoint(id, payload);
      const ok = Boolean(res?.data?.status);
      if (ok) refresh();
      return ok;
    } catch {
      return false;
    }
  }, [refresh]);

  const remove = useCallback(async (id) => {
    try {
      const res = await deleteWebhookEndpoint(id);
      const ok = Boolean(res?.data?.status);
      if (ok) refresh();
      return ok;
    } catch {
      return false;
    }
  }, [refresh]);

  const rotate = useCallback(async (id) => {
    try {
      const created = toCreated(await rotateWebhookSecret(id));
      if (created) refresh();
      return created;
    } catch {
      return null;
    }
  }, [refresh]);

  // { kind: 'success' | 'targetError' | 'failed', responseStatus?, error? }
  const test = useCallback(async (id) => {
    try {
      const res = await testWebhookEndpoint(id);
      const result = res?.data?.data;
      if (!res?.data?.status || !result) return { kind: 'failed' };
      return result.status
        ? { kind: 'success', responseStatus: result.responseStatus }
        : { kind: 'targetError', responseStatus: result.responseStatus, error: result.error };
    } catch {
      return { kind: 'failed' };
    }
  }, []);

  return {
    fetching, failed, data, catalog, refresh, create, update, remove, rotate, test,
  };
}

// Teslimat günlüğü — sunucu tarafı sayfalama (1 tabanlı) + durum süzgeci.
export function useWebhookDeliveries(endpointId) {
  const [fetching, setFetching] = useState(false);
  const [failed, setFailed] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState({ page: 1, pageSize: 20, status: null });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const requestSeq = useRef(0);

  // Başka bir uca geçildiğinde süzgeç/sayfa sıfırlanır. Zaten başlangıç değerindeyse AYNI
  // nesne döner — yeni bir nesne açılışta ikinci (gereksiz) bir istek tetiklerdi.
  useEffect(() => {
    setQuery((q) => (q.page === 1 && q.pageSize === 20 && q.status === null
      ? q
      : { page: 1, pageSize: 20, status: null }));
    setRows([]);
    setTotal(0);
  }, [endpointId]);

  useEffect(() => {
    if (!endpointId) return;
    const seq = requestSeq.current + 1;
    requestSeq.current = seq;
    setFetching(true);
    getWebhookDeliveries(endpointId, query)
      .then((res) => {
        if (seq !== requestSeq.current) return;
        if (!res?.data?.status) {
          setFailed(true);
          setRows([]);
          setTotal(0);
          return;
        }
        setFailed(false);
        setRows(extractListData(res));
        setTotal(res.data.data?.totalRowCount || 0);
      })
      .finally(() => { if (seq === requestSeq.current) setFetching(false); });
  }, [endpointId, query, refreshTrigger]);

  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  const replay = useCallback(async (deliveryId) => {
    try {
      const res = await replayWebhookDelivery(deliveryId);
      const ok = Boolean(res?.data?.status);
      if (ok) refresh();
      return ok;
    } catch {
      return false;
    }
  }, [refresh]);

  return {
    fetching, failed, rows, total, query, setQuery, refresh, replay,
  };
}
